import { styled } from "goober";
import {
  type DisplayableSpace,
  useSpaceDisplayName,
} from "../../hooks/useSpaceDisplayName";
import { useNow } from "../../hooks/useNow";
import { formatRelativeTime } from "../../lib/formatRelativeTime";
import { spaceIcon } from "../../lib/spaceIcon";
import { Icon } from "../Icon";
import { metaJoin } from "../MetaSeparator";
import { SmallWorkflowBadge } from "../SmallWorkflowBadge";
import { StatusText } from "../StatusText";
import { variantStyles } from "../Text";
import { PulseDot } from "../WorkflowSubtext";
import { pickChatIconColor } from "./pickChatIconColor";

export type ChatRowData = {
  id: string;
  name: string;
  lastActivityAt: number | null;
  pending?: boolean;
  workflow?: {
    id: string;
    name: string;
    spaceId: string;
    badgeSeed: string | null;
    pausedAt: number | null;
    branchId?: string;
    branchName?: string;
    space?: DisplayableSpace;
  } | null;
};

// The branch name gives way first so the space stays readable.
const Subtitle = styled("span")`
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  color: var(--text-secondary);
  & > span {
    flex-shrink: 0;
    overflow: hidden;
  }
  & > span:first-child {
    flex-shrink: 1;
  }
  & > span > span:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
  }
  & > span > :not(:last-child) {
    flex-shrink: 0;
  }
`;

export function ChatRowBadge({ chat }: { chat: ChatRowData }) {
  return chat.workflow ? (
    <SmallWorkflowBadge
      workflow={chat.workflow}
      blueprint={!!chat.workflow.branchName}
    />
  ) : (
    <Icon
      name="ask"
      size={16}
      color={`var(--${pickChatIconColor(chat.id)}-600)`}
      style={{ flexShrink: 0 }}
    />
  );
}

// Which version a workflow row opens and where it lives; when a chat last moved.
export function ChatRowSubtitle({ chat }: { chat: ChatRowData }) {
  const displayName = useSpaceDisplayName();
  const now = useNow(60_000);
  return (
    <Subtitle style={variantStyles["text-small"]}>
      {chat.workflow
        ? metaJoin([
            chat.workflow.branchName ? (
              <StatusText>
                <Icon name="git-branch" size={10} />
                <span>{chat.workflow.branchName}</span>
              </StatusText>
            ) : (
              <StatusText>
                <PulseDot paused={chat.workflow.pausedAt != null} />
                {chat.workflow.pausedAt != null ? "Paused" : "Live"}
              </StatusText>
            ),
            chat.workflow.space && (
              <StatusText>
                <Icon name={spaceIcon(chat.workflow.space.mode)} size={10} />
                <span>{displayName(chat.workflow.space)}</span>
              </StatusText>
            ),
          ])
        : chat.lastActivityAt != null &&
          !chat.pending &&
          formatRelativeTime(chat.lastActivityAt, now)}
    </Subtitle>
  );
}
